import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option, Constants } from '@/src/utils';
import {
    type StatusReportInitialOptions,
    type StatusReportSchoolClassOptions,
    type ClassOption,
    type StatusReportFilterState,
    type ExamOption,
} from '../models/statusReportFilterDTO';
import {
    SimpleButton,
    SimpleSingleSelect,
    SimpleRadio,
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
} from '@/src/ui';

type GroupBy = 'examClass' | 'exam';

interface StatusReportFiltersProps {
    refreshTableData: (filters: StatusReportFilterState) => void | Promise<void>;
    filterState: StatusReportFilterState;
    setFilterState: React.Dispatch<React.SetStateAction<StatusReportFilterState>>;
    onExamNameChange?: (name: string) => void;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectSchoolOption = getZeroOption('Select School');
const SelectClassOption = getZeroOption('Select Class');
const SelectGradeOption = getZeroOption('Select Grade');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');
const SelectDistrictOption = getZeroOption('Select District');
const SelectExamOption = getZeroOption('All Exams');

/** Map GroupBy <-> numeric ids because SimpleSingleSelect.value is number-typed */
const GROUP_BY_TO_ID: Record<GroupBy, number> = { examClass: 0, exam: 1 };
const ID_TO_GROUP_BY: Record<number, GroupBy> = { 0: 'examClass', 1: 'exam' };

const groupByOptions = [
    { id: GROUP_BY_TO_ID.examClass, name: 'By Class' },
    { id: GROUP_BY_TO_ID.exam, name: 'By Exam' },
];

const StatusReportFilters: React.FC<StatusReportFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
    onExamNameChange,
}) => {
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [classData, setClassData] = useState<ClassOption[]>([]);

    const [filtersLoading, setFiltersLoading] = useState(true);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const [examDataAll, setExamDataAll] = useState<ExamOption[] | null>(null);
    const [examOptions, setExamOptions] = useState<Option[]>([SelectExamOption]);
    const [examLoading, setExamLoading] = useState(false);

    const requiredFields = ['districtId', 'schoolId', 'gradeLevelMasterId'] as const;
    function getMissingFields(fs: any): string[] {
        return requiredFields
            .filter((key) => !fs[key] || Number(fs[key]) === 0)
            .map((key) => Constants.fieldLabels[key]);
    }

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<StatusReportInitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;
                setGradeLevelMasterOptions([SelectGradeOption, ...data.gradeLevelMasterOptions]);
                const sortedDistricts = data.districtOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setDistrictOptions([SelectDistrictOption, ...sortedDistricts]);
                setExamTypeMasterOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);

                if (
                    filterState &&
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(
                        filterState.districtId,
                        filterState.schoolId,
                        filterState.classId
                    );
                }
            } catch {
                setDistrictOptions([AllOption]);
                setGradeLevelMasterOptions([SelectGradeOption]);
                setExamTypeMasterOptions([AllOption]);
                setErrorMsg(
                    'Failed to load initial filter options. Please refresh the page or try again.'
                );
            } finally {
                setFiltersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    const fetchSchoolClassOptions = async (
        districtId: number | string,
        savedSchoolId?: number,
        savedClassId?: number
    ) => {
        if (!districtId) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<StatusReportSchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;

            const sortedSchools = data.schoolOptions.sort((a, b) => a.name.localeCompare(b.name));
            setSchoolOptions([SelectSchoolOption, ...sortedSchools]);
            const validSchoolId =
                savedSchoolId && sortedSchools.some((opt) => opt.id === savedSchoolId)
                    ? savedSchoolId
                    : Number(SelectSchoolOption.id);
            setFilterState((prev) => ({ ...prev, schoolId: Number(validSchoolId) }));

            const sortedClassData = data.classOptions.sort((a, b) => a.name.localeCompare(b.name));
            setClassData(sortedClassData);
            let filteredClassOptions = sortedClassData;
            if (validSchoolId && validSchoolId !== Number(SelectSchoolOption.id)) {
                filteredClassOptions = sortedClassData.filter(
                    (opt) => opt.schoolId === Number(validSchoolId)
                );
            }
            const gradeId = filterState.gradeLevelMasterId;
            if (gradeId && gradeId !== 0) {
                filteredClassOptions = filteredClassOptions.filter(
                    (opt) =>
                        Array.isArray(opt.gradeLevelMasterIds) &&
                        opt.gradeLevelMasterIds.includes(gradeId)
                );
            }
            filteredClassOptions = filteredClassOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            const classOptionsArr = filteredClassOptions.length
                ? [
                      SelectClassOption,
                      ...filteredClassOptions.map((opt) => ({ id: opt.id, name: opt.name })),
                  ]
                : [NoClassOption];
            setClassOptions(classOptionsArr);

            const validClassId =
                savedClassId && filteredClassOptions.some((opt) => opt.id === savedClassId)
                    ? savedClassId
                    : Number(SelectClassOption.id);
            setFilterState((prev) => ({ ...prev, classId: Number(validClassId) }));
            setErrorMsg(null);
        } catch {
            setErrorMsg(
                'Failed to fetch school and class options. Please refresh the page or try again.'
            );
            setSchoolOptions([AllOption]);
            setClassOptions([AllOption]);
        }
    };

    const updateClassOptions = (
        data: Array<ClassOption>,
        gradeLevelMasterId: number | string,
        schoolId: number | string
    ) => {
        if (
            !gradeLevelMasterId ||
            gradeLevelMasterId === Number(SelectGradeOption.id) ||
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id)
        ) {
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(SelectClassOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter((option) => {
                if (schoolId && option.schoolId !== Number(schoolId)) return false;
                const glids = option.gradeLevelMasterIds;
                const doGradeOptionsExist = glids && glids.length > 0;
                if (
                    gradeLevelMasterId &&
                    doGradeOptionsExist &&
                    !glids.includes(Number(gradeLevelMasterId))
                )
                    return false;
                return true;
            })
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
            return;
        }

        setClassOptions([SelectClassOption, ...newClassOptions]);

        setFilterState((prev) => {
            const classExists = newClassOptions.some(
                (opt) => Number(opt.id) === Number(prev.classId)
            );
            return {
                ...prev,
                classId: classExists ? prev.classId : Number(SelectClassOption.id),
            };
        });
    };

    // --- Exam Pipeline ---
    const maybeFetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setExamDataAll(null);
            setExamLoading(false);
            return;
        }

        setExamLoading(true);
        try {
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );

            const data = (res.data.examOptions ?? []).filter((exam) => exam.schoolIds?.length);
            const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
            setExamDataAll(sorted);
        } catch {
            setExamDataAll([]);
            setErrorMsg('Failed to load exam options. Please refresh the page or try again.');
        } finally {
            setExamLoading(false);
        }
    };

    const filterExamsLocally = (
        schoolId: number,
        classId: number,
        examTypeMasterId: number,
        allExamsData: ExamOption[] | null
    ) => {
        if (!allExamsData) return;

        if (allExamsData.length === 0) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        let filtered = allExamsData;

        if (schoolId && schoolId !== Number(SelectSchoolOption.id)) {
            filtered = filtered.filter(
                (e) => !e.schoolIds?.length || e.schoolIds.includes(schoolId)
            );
        }

        if (
            classId &&
            classId !== Number(SelectClassOption.id) &&
            classId !== Number(AllOption.id)
        ) {
            filtered = filtered.filter((e) => !e.classIds?.length || e.classIds.includes(classId));
        }

        if (examTypeMasterId && examTypeMasterId !== Number(AllOption.id)) {
            filtered = filtered.filter((e) => e.examTypeMasterId === examTypeMasterId);
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
        } else {
            setExamOptions([SelectExamOption, ...filtered]);
            setFilterState((prev) => {
                const examExists = filtered.some((e) => Number(e.id) === Number(prev.examId));
                return {
                    ...prev,
                    examId: examExists ? prev.examId : Number(SelectExamOption.id),
                };
            });
        }
    };

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
    }, [filterState.gradeLevelMasterId]);

    // Filter exams locally when dependent fields or fetched exams change
    useEffect(() => {
        filterExamsLocally(
            filterState.schoolId,
            filterState.classId,
            filterState.examTypeMasterId,
            examDataAll
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examDataAll, filterState.schoolId, filterState.classId, filterState.examTypeMasterId]);

    // Helper to show loading state for dropdowns
    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            districtId: newDistrictId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        });
                        fetchSchoolClassOptions(newDistrictId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        }));
                        updateClassOptions(classData, Number(SelectGradeOption.id), newSchoolId);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.districtId ||
                        filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-level-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            gradeLevelMasterId: newGradeLevelMasterId,
                            classId: Number(SelectClassOption.id),
                        }));
                        updateClassOptions(classData, newGradeLevelMasterId, filterState.schoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.schoolId ||
                        filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, classId: newClassId }));
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.districtId ||
                        filterState.districtId === Number(SelectDistrictOption.id) ||
                        !filterState.schoolId ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        !filterState.gradeLevelMasterId ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <FormControl size="small" sx={{ minWidth: 140, maxWidth: 280, mb: 0.5, mt: '4px' }}>
                    <InputLabel
                        shrink
                        htmlFor="group-by-radio"
                        sx={{ background: 'white', px: 0.5 }}
                    >
                        View Mode
                    </InputLabel>
                    <Box
                        id="group-by-radio"
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: 1,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            px: 2,
                            py: 1.5,
                            maxHeight: 40,
                            alignSelf: 'center',
                        }}
                    >
                        {groupByOptions.map((option) => (
                            <SimpleRadio
                                key={option.id}
                                name="group-by"
                                label={option.name}
                                value={String(option.id)}
                                checked={GROUP_BY_TO_ID[filterState.groupBy] === option.id}
                                onChange={() => {
                                    setFieldsChanged(true);
                                    setFilterState((prev) => ({
                                        ...prev,
                                        groupBy: ID_TO_GROUP_BY[option.id],
                                    }));
                                }}
                                sx={{ marginRight: 0 }}
                            />
                        ))}
                    </Box>
                </FormControl>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e: any) => {
                        setFieldsChanged(true);
                        const newExamTypeMasterId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            examTypeMasterId: newExamTypeMasterId,
                        }));
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280, height: 40, alignSelf: 'center' }}
                    disabled={filtersLoading}
                />
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment"
                    {...getLoadingProps(
                        examOptions,
                        Number(
                            getValidSelectValue(
                                filterState.examId,
                                examOptions,
                                examOptions[0]?.id ?? 0
                            )
                        ),
                        examLoading || filtersLoading
                    )}
                    onChange={(e: any) => {
                        setFieldsChanged(true);
                        const value = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, examId: value }));
                        onExamNameChange?.(
                            examOptions.find((o) => Number(o.id) === value)?.name ?? ''
                        );
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        examLoading ||
                        filtersLoading ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(
                                `Please select the following required fields before filtering: ${missing.join(', ')}.`
                            );
                            return;
                        }
                        setErrorMsg(null);
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default StatusReportFilters;
